import { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import InviteDialog from "@/components/InviteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, ShieldOff, User, Upload, X, CheckCircle2, AlertCircle, Mail, Clock, RotateCw, Trash2 } from "lucide-react";
import WorkerAvatar from "@/components/WorkerAvatar";
import BulkImportDialog from "@/components/BulkImportDialog";

export default function TeamMembers() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [inviteDefault, setInviteDefault] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const isTeamMember = user?.role === "team_member";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [teamRes, pendingRes] = await Promise.all([
        isTeamMember ? Promise.resolve({ data: [] }) : api.get("/team"),
        api.get("/team/pending").catch(() => ({ data: [] })),
      ]);
      setTeam(teamRes.data);
      setPending(pendingRes.data || []);
    } finally {
      setLoading(false);
    }
  }, [isTeamMember]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AppLayout>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-caps text-slate-500">Team</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mt-1">
            Managers &amp; Team Members
          </h1>
          <p className="text-slate-600 mt-1.5">
            All team members can review documents by default. Managers can invite additional reviewers or managers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { setInviteDefault("worker"); setOpen(true); }}
            data-testid="invite-worker-btn"
            className="rounded-full h-10 px-4"
          >
            <User className="h-4 w-4 mr-1.5" /> Invite worker
          </Button>
          {!isTeamMember && (
            <Button
              variant="outline"
              onClick={() => setBulkOpen(true)}
              data-testid="bulk-import-btn"
              className="rounded-full h-10 px-4"
            >
              <Upload className="h-4 w-4 mr-1.5" /> Bulk import
            </Button>
          )}
          {!isTeamMember && (
            <Button
              onClick={() => { setInviteDefault("team_member"); setOpen(true); }}
              data-testid="invite-btn"
              className="bg-teal-700 hover:bg-teal-800 rounded-full h-10 px-5"
            >
              <UserPlus className="h-4 w-4 mr-1.5" /> Add teammate
            </Button>
          )}
        </div>
      </div>

      <PendingInvitesPanel pending={pending} onChanged={load} isTeamMember={isTeamMember} />

      {!isTeamMember && (
        <div className="mt-8 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-5 py-3 label-caps">Name</th>
              <th className="text-left px-5 py-3 label-caps">Email</th>
              <th className="text-left px-5 py-3 label-caps">Role</th>
              <th className="text-left px-5 py-3 label-caps">2FA</th>
              <th className="text-left px-5 py-3 label-caps">Auth</th>
              <th className="text-left px-5 py-3 label-caps">Joined</th>
              <th className="text-right px-5 py-3 label-caps">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-slate-500">Loading…</td>
              </tr>
            )}
            {!loading &&
              team.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`team-row-${u.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <WorkerAvatar workerId={u.id} name={u.name} size="sm" />
                      <div className="flex items-center gap-2">
                        {u.role === "manager" ? (
                          <ShieldCheck className="h-4 w-4 text-teal-700" />
                        ) : (
                          <User className="h-4 w-4 text-slate-500" />
                        )}
                        <span className="font-semibold text-slate-900">{u.name}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-slate-700">{u.email}</td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                        u.role === "manager"
                          ? "bg-teal-50 text-teal-700 border-teal-200"
                          : "bg-slate-100 text-slate-700 border-slate-200"
                      }`}
                    >
                      {u.role === "manager" ? "Manager" : "Team member"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    {u.totp_enabled ? (
                      <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] uppercase tracking-widest font-semibold" data-testid={`team-2fa-on-${u.id}`}>
                        <ShieldCheck className="h-3 w-3" /> On
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 bg-slate-50 text-slate-500 border-slate-200 text-[10px] uppercase tracking-widest font-semibold">
                        Off
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-slate-600 text-xs uppercase tracking-wider">{u.auth_provider}</td>
                  <td className="px-5 py-4 text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-4 text-right">
                    {u.totp_enabled && u.id !== user?.id && (
                      <ResetTwoFactorButton user={u} onDone={load} />
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      )}

      <PendingInvitesPanel pending={pending} onChanged={load} isTeamMember={isTeamMember} />

      <InviteDialog
        open={open}
        onOpenChange={setOpen}
        defaultRole={inviteDefault || undefined}
        availableRoles={isTeamMember ? ["worker"] : ["worker", "team_member", "manager"]}
        onCreated={load}
      />
      <BulkImportDialog open={bulkOpen} onOpenChange={setBulkOpen} onImported={load} />
    </AppLayout>
  );
}


function PendingInvitesPanel({ pending, onChanged, isTeamMember }) {
  const [busy, setBusy] = useState(null);

  const resend = async (u) => {
    setBusy(u.id);
    try {
      await api.post(`/team/pending/${u.id}/resend`);
      toast.success("Invite email resent", { description: `New link sent to ${u.email}` });
      onChanged && onChanged();
    } catch (e) {
      toast.error("Could not resend", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (u) => {
    if (!window.confirm(`Cancel the invite for ${u.email}? This deletes their pending account.`)) return;
    setBusy(u.id);
    try {
      await api.delete(`/team/pending/${u.id}`);
      toast.success("Invite cancelled");
      onChanged && onChanged();
    } catch (e) {
      toast.error("Could not cancel", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(null);
    }
  };

  if (!pending || pending.length === 0) return null;

  return (
    <section className="mt-8 rounded-xl border border-amber-200 bg-amber-50/40 overflow-hidden" data-testid="pending-invites">
      <div className="flex items-center gap-2 px-5 py-3 bg-amber-50 border-b border-amber-200">
        <Mail className="h-4 w-4 text-amber-700" />
        <div className="font-heading font-semibold text-slate-900">Pending invites</div>
        <div className="text-xs text-amber-800">{pending.length} awaiting activation</div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-white/60 text-slate-500">
          <tr>
            <th className="text-left px-5 py-2.5 label-caps">Name</th>
            <th className="text-left px-5 py-2.5 label-caps">Email</th>
            <th className="text-left px-5 py-2.5 label-caps">Role</th>
            <th className="text-left px-5 py-2.5 label-caps">Invited</th>
            <th className="text-right px-5 py-2.5 label-caps">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pending.map((u) => {
            const invitedAt = u.invited_at ? new Date(u.invited_at) : null;
            const expiresAt = u.invite_expires_at ? new Date(u.invite_expires_at) : null;
            const expired = expiresAt && expiresAt < new Date();
            const canManage = !isTeamMember || u.role === "worker";
            return (
              <tr key={u.id} className="border-t border-amber-100" data-testid={`pending-row-${u.id}`}>
                <td className="px-5 py-3 font-semibold text-slate-900">{u.name}</td>
                <td className="px-5 py-3 text-slate-700">{u.email}</td>
                <td className="px-5 py-3">
                  <span className="inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold bg-white text-slate-700 border-slate-200">
                    {u.role === "worker" ? (u.worker_role ? u.worker_role.replaceAll("_", " ") : "Worker") : u.role.replace("_", " ")}
                  </span>
                </td>
                <td className="px-5 py-3 text-slate-600 text-xs">
                  {invitedAt ? invitedAt.toLocaleDateString() : "—"}
                  {expiresAt && (
                    <div className={`text-[0.7rem] mt-0.5 flex items-center gap-1 ${expired ? "text-rose-600" : "text-amber-700"}`}>
                      <Clock className="h-3 w-3" />
                      {expired ? "Link expired — resend below" : `Link valid until ${expiresAt.toLocaleDateString()}`}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resend(u)}
                      disabled={!canManage || busy === u.id}
                      data-testid={`pending-resend-${u.id}`}
                      className="h-8 px-3"
                    >
                      <RotateCw className={`h-3.5 w-3.5 mr-1 ${busy === u.id ? "animate-spin" : ""}`} /> Resend
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancel(u)}
                      disabled={!canManage || busy === u.id}
                      data-testid={`pending-cancel-${u.id}`}
                      className="h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function ResetTwoFactorButton({ user, onDone }) {
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    if (!window.confirm(`Reset 2FA for ${user.name}? They'll need to re-enrol on their next sign-in.`)) return;
    setBusy(true);
    try {
      await api.post(`/team/${user.id}/reset-2fa`);
      toast.success("2FA reset", { description: `${user.email} will re-enrol on next login.` });
      onDone && onDone();
    } catch (e) {
      toast.error("Could not reset", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={reset}
      disabled={busy}
      data-testid={`team-reset-2fa-${user.id}`}
      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
    >
      <ShieldOff className="h-3.5 w-3.5 mr-1" /> Reset 2FA
    </Button>
  );
}

